import { Heart, ShieldCheck, Sparkles, Users, BookOpen, Award } from "lucide-react";
import { SITE, IMG } from "@/lib/site";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import CtaSection from "@/components/CtaSection";

const valori = [
  { icon: Heart, t: "Amore per gli animali", d: "Il punto di partenza di tutto ciò che facciamo." },
  { icon: ShieldCheck, t: "Professionalità", d: "Vent'anni di esperienza e competenza." },
  { icon: Sparkles, t: "Cura nei dettagli", d: "Ogni gesto pensato per il benessere." },
  { icon: Users, t: "Rapporto umano", d: "Trattiamo ogni cliente come un amico." },
  { icon: BookOpen, t: "Consulenza continua", d: "Prima, durante e dopo la scelta." },
  { icon: Award, t: "Riconoscimento ENCI", d: "Dal 2000 al fianco del mondo cinofilo." },
];

export default function ChiSiamo() {
  return (
    <div data-testid="page-chi-siamo">
      <PageHero
        overline="Chi siamo"
        title="La passione per gli animali, ogni giorno."
        lead="Da oltre vent'anni Nicola Bombana, con la moglie Elisa, dedica la propria vita ai cani: un mestiere nato dalla passione, che continua ad evolvere."
        image={IMG.cristal}
        primaryCta={{ to: "/contatti", label: "Vieni a conoscerci" }}
        secondaryCta={{ to: "/allevamento", label: "Vedi l'allevamento" }}
      />

      <section className="py-16 sm:py-24" data-testid="storia">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 reveal">
            <SectionHeading overline="La nostra storia" title="Una passione divenuta professione." />
          </div>
          <div className="lg:col-span-7 reveal space-y-5 text-[#6B5F4A] leading-relaxed text-lg">
            <p>
              Tutto è iniziato dalla passione per il pastore tedesco. Una
              passione che, grazie all'impegno quotidiano, è diventata un lavoro
              a tempo pieno e un punto di riferimento per chi cerca un cucciolo
              o un servizio dedicato per il proprio cane.
            </p>
            <p>
              L'allevamento è attivo e riconosciuto dall'ENCI da oltre vent'anni.
              Dal 2024 alla nostra attività di allevamento si è affiancata la
              <strong> pensione per cani</strong>, aperta tutto l'anno, e nel
              tempo abbiamo ampliato i nostri servizi includendo
              <strong> toelettatura</strong>, <strong>vendita di cibo per cani</strong>,
              <strong> negozio per animali</strong> e <strong>articoli da pesca</strong>.
            </p>
            <p>
              Titolare della struttura è <strong>{SITE.owner}</strong>, affiancato
              dalla moglie {SITE.partner}: insieme hanno costruito un luogo in
              cui i cani vengono accolti, accuditi e seguiti come membri della
              famiglia.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#EDD895]/60" data-testid="missione">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-12 text-center reveal">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#7A1F1F] mb-5">
            La nostra missione
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1710] leading-tight">
            Offrire servizi completi e affidabili <em className="italic text-[#7A1F1F]">per animali e proprietari</em>.
          </h2>
          <p className="mt-6 text-lg text-[#6B5F4A] leading-relaxed">
            Essere il punto di riferimento per chi desidera prendersi cura del
            proprio cane con competenza, calore e dedizione. Un servizio completo,
            costruito attorno al benessere dell'animale e alla serenità della famiglia.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="valori">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <SectionHeading overline="I nostri valori" title="Ciò in cui crediamo." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {valori.map((v, i) => (
              <div
                key={v.t}
                className="reveal bg-[#F5E6C0] border border-[#D9C48A] rounded-3xl p-7"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="w-12 h-12 rounded-2xl bg-[#EDD895] grid place-items-center mb-5 text-[#8B4A2B]">
                  <v.icon className="w-6 h-6" strokeWidth={1.4} />
                </div>
                <h4 className="font-sans font-semibold text-[#1A1710] text-lg">{v.t}</h4>
                <p className="mt-2 text-sm text-[#6B5F4A] leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#EDD895]/60" data-testid="riferimento">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden">
              <img src={IMG.hero} alt="Colli Storici" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-6 reveal">
            <SectionHeading
              overline="Un punto di riferimento completo"
              title="Sei attività, un'unica famiglia."
              lead="Allevamento, toelettatura, pensione, alimentazione, negozio per animali e articoli da pesca: tutto unito da una sola passione, l'amore per gli animali."
            />
          </div>
        </div>
      </section>

      <CtaSection
        title="Vieni a conoscere Colli Storici"
        lead="Saremo felici di accogliere te e il tuo amico a quattro zampe. Ti aspettiamo."
        primaryLabel="Contattaci"
      />
    </div>
  );
}
